import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import ReactPlayer from "react-player";
import type { OnProgressProps } from "react-player/base";
import { Button, Card, CardContent, Progress, Badge } from "@learn-me/ui";
import { useCourse } from "@/features/courses/hooks/useCourses";
import {
  useChapter,
  useMarkChapterComplete,
  useQuiz,
  useStartQuizAttempt,
  useSubmitQuizAttempt,
  useUpdateVideoProgress,
} from "@/features/learning/hooks/useLearning";
import { learningApi } from "@/features/learning/api/learningApi";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Lock,
  PlayCircle,
  FileText,
  Download,
  Menu,
  X,
  Clock,
  AlertCircle,
} from "lucide-react";
import {
  AudioPlayer,
  InteractiveContent,
  SectionPlayer,
  PaymentStatusBanner,
} from "@/components/content";
import {
  BadgeCelebration,
  CourseCompleteCelebration,
  type NewBadge,
} from "@/features/gamification";
import { motion } from "framer-motion";

export default function CoursePlayerPage() {
  const { courseSlug, chapterId } = useParams<{
    courseSlug: string;
    chapterId?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep linking para secciones
  const sectionFromUrl = useMemo(() => {
    const sectionParam = searchParams.get("section");
    return sectionParam ? Math.max(0, parseInt(sectionParam, 10) - 1) : null;
  }, []);

  const [resolvedInitialSection, setResolvedInitialSection] = useState<number>(
    sectionFromUrl ?? 0,
  );
  const [hasFetchedResume, setHasFetchedResume] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(
    sectionFromUrl ?? 0,
  );
  const [sectionGating, setSectionGating] = useState<
    | {
        maxAccessiblePosition: number;
        requiresSequential: boolean;
      }
    | undefined
  >(undefined);
  const [chapterSectionProgress, setChapterSectionProgress] = useState<
    | Awaited<ReturnType<typeof learningApi.getChapterSectionProgress>>
    | undefined
  >(undefined);

  const handleSectionChange = useCallback(
    (sectionIndex: number) => {
      setCurrentSectionIndex(sectionIndex);
      setResolvedInitialSection(sectionIndex);
      // Actualizar URL con la sección actual (1-indexed para el usuario)
      setSearchParams(
        (prev) => {
          if (sectionIndex === 0) {
            prev.delete("section");
          } else {
            prev.set("section", String(sectionIndex + 1));
          }
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // En mobile el sidebar arranca cerrado para no tapar la pantalla al entrar
  // al curso. En desktop (≥1024px) arranca abierto.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string[]>>({});
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Celebraciones
  const [newBadges, setNewBadges] = useState<NewBadge[]>([]);
  const [showCourseComplete, setShowCourseComplete] = useState(false);
  const [showMilestone, setShowMilestone] = useState<number | null>(null);
  const lastMilestone = useRef<number>(0);

  const { data: course, isLoading: courseLoading } = useCourse(courseSlug!);
  const { data: chapter, isLoading: chapterLoading } = useChapter(
    chapterId || "",
  );
  const { data: quiz } = useQuiz(chapterId || "");
  const markCompleteMutation = useMarkChapterComplete();
  const startQuizMutation = useStartQuizAttempt();
  const submitQuizMutation = useSubmitQuizAttempt();
  const updateProgressMutation = useUpdateVideoProgress();

  // Video progress tracking
  const playerRef = useRef<ReactPlayer>(null);
  const lastSavedPosition = useRef<number>(0);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [hasResumed, setHasResumed] = useState(false);

  // Save video progress every 10 seconds
  const handleVideoProgress = useCallback(
    (state: OnProgressProps) => {
      if (!chapterId) return;

      const currentPosition = Math.floor(state.playedSeconds);

      // Only save if position changed by at least 5 seconds
      if (Math.abs(currentPosition - lastSavedPosition.current) >= 5) {
        lastSavedPosition.current = currentPosition;
        updateProgressMutation.mutate({
          chapterId,
          lastPosition: currentPosition,
          videoProgress: Math.floor(state.played * 100),
        });
      }
    },
    [chapterId, updateProgressMutation],
  );

  // Resume from last position when chapter loads
  useEffect(() => {
    if (
      isPlayerReady &&
      !hasResumed &&
      chapter?.userProgress?.lastPosition &&
      chapter.userProgress.lastPosition > 0 &&
      playerRef.current
    ) {
      playerRef.current.seekTo(chapter.userProgress.lastPosition, "seconds");
      setHasResumed(true);
    }
  }, [isPlayerReady, hasResumed, chapter?.userProgress?.lastPosition]);

  // Reset resume state when chapter changes + flush del último lastPosition
  // mediante fetch keepalive (similar a sendBeacon, pero permite PUT y headers).
  useEffect(() => {
    return () => {
      // Cleanup al cambiar de capítulo / unmount: enviar la última posición
      // antes de que el cliente la olvide. Hasta 10s pueden quedar sin guardar
      // por el throttle de 5s + interval del player.
      if (
        chapterId &&
        playerRef.current &&
        lastSavedPosition.current > 0 &&
        typeof navigator !== "undefined"
      ) {
        try {
          const pos = Math.floor(
            playerRef.current.getCurrentTime?.() ?? lastSavedPosition.current,
          );
          if (pos > 0) {
            const baseUrl = import.meta.env.VITE_API_URL || "/api";
            const token = localStorage.getItem("accessToken");
            void fetch(`${baseUrl}/chapters/${chapterId}/progress`, {
              method: "PUT",
              keepalive: true,
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({ lastPosition: pos }),
            }).catch(() => {
              /* best-effort */
            });
          }
        } catch {
          /* best-effort */
        }
      }
    };
  }, [chapterId]);

  useEffect(() => {
    setHasResumed(false);
    setIsPlayerReady(false);
    lastSavedPosition.current = 0;
    setHasFetchedResume(false);
    // Solo resetear la sección si no hay parámetro en URL
    if (sectionFromUrl === null) {
      setResolvedInitialSection(0);
      setCurrentSectionIndex(0);
    }
  }, [chapterId, sectionFromUrl]);

  // Fetch resume position for sections (if no explicit section in URL)
  useEffect(() => {
    if (!chapterId || sectionFromUrl !== null || hasFetchedResume) return;

    const fetchResumePosition = async () => {
      try {
        const result = await learningApi.getResumePosition(chapterId);
        if (result?.position) {
          // Convertir a índice 0-based
          const resumeIndex = result.position - 1;
          setResolvedInitialSection(resumeIndex);
          setCurrentSectionIndex(resumeIndex);
        }
      } catch (error) {
        // Silenciosamente ignorar errores de resume
        console.debug("Could not fetch resume position:", error);
      } finally {
        setHasFetchedResume(true);
      }
    };

    fetchResumePosition();
  }, [chapterId, sectionFromUrl, hasFetchedResume]);

  // Fetch section gating info
  useEffect(() => {
    if (!chapterId) return;

    const fetchSectionState = async () => {
      try {
        const [gating, progress] = await Promise.all([
          learningApi.getSectionGating(chapterId),
          learningApi.getChapterSectionProgress(chapterId),
        ]);
        setSectionGating(gating);
        setChapterSectionProgress(progress);
      } catch (error) {
        console.debug("Could not fetch section state:", error);
      }
    };

    fetchSectionState();
  }, [chapterId]);

  // Callback para marcar sección como vista
  const handleSectionViewed = useCallback(
    async (sectionId: string) => {
      if (!chapterId) return false;
      try {
        await learningApi.markSectionViewed(sectionId);
        // Refresh gating info after marking section as viewed
        const [gating, progress] = await Promise.all([
          learningApi.getSectionGating(chapterId),
          learningApi.getChapterSectionProgress(chapterId),
        ]);
        setSectionGating(gating);
        setChapterSectionProgress(progress);
        return true;
      } catch (error) {
        console.debug("Could not mark section as viewed:", error);
        return false;
      }
    },
    [chapterId],
  );

  // Heartbeat de tiempo en la sección actual.
  // El backend exige que el alumno acumule un mínimo de segundos en la
  // sección (vía updateSectionTimeSpent) antes de aceptar markSectionViewed.
  // Sin este heartbeat el primer intento de marcar la sección falla con
  // SECTION_NOT_READY y el capítulo nunca puede completarse.
  useEffect(() => {
    const sectionId = chapter?.sections?.[currentSectionIndex]?.id;
    if (!sectionId || !chapterId) return;

    const HEARTBEAT_SECONDS = 5;
    const VIEW_THRESHOLD_SECONDS = 60;
    let elapsed = 0;
    let viewedMarked = false;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      elapsed += HEARTBEAT_SECONDS;
      try {
        const timeProgress = await learningApi.updateSectionTimeSpent(
          sectionId,
          HEARTBEAT_SECONDS,
        );
        setChapterSectionProgress((current) =>
          current
            ? {
                ...current,
                sections: current.sections.map((section) =>
                  section.sectionId === sectionId
                    ? {
                        ...section,
                        timeSpent: timeProgress.timeSpent,
                        requiredSeconds: timeProgress.requiredSeconds,
                        remainingSeconds: timeProgress.remainingSeconds,
                        isViewed: timeProgress.isViewed,
                        viewedAt: timeProgress.viewedAt,
                      }
                    : section,
                ),
              }
            : current,
        );
        if (!viewedMarked && timeProgress.isViewed) {
          viewedMarked = true;
          const [gating, progress] = await Promise.all([
            learningApi.getSectionGating(chapterId),
            learningApi.getChapterSectionProgress(chapterId),
          ]);
          if (!cancelled) {
            setSectionGating(gating);
            setChapterSectionProgress(progress);
          }
        }
      } catch {
        // ignorar fallos transitorios; reintentamos en el próximo tick
      }
      if (cancelled) return;
      if (!viewedMarked && elapsed >= VIEW_THRESHOLD_SECONDS) {
        try {
          await learningApi.markSectionViewed(sectionId);
          viewedMarked = true;
          try {
            const [gating, progress] = await Promise.all([
              learningApi.getSectionGating(chapterId),
              learningApi.getChapterSectionProgress(chapterId),
            ]);
            if (!cancelled) {
              setSectionGating(gating);
              setChapterSectionProgress(progress);
            }
          } catch {
            // ignorar
          }
        } catch {
          // todavía no es elegible (p. ej. sección larga); seguimos enviando
          // heartbeats y reintentaremos en próximos ticks.
        }
      }
    };

    const interval = setInterval(tick, HEARTBEAT_SECONDS * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [chapterId, currentSectionIndex, chapter?.sections]);

  // Auto-select first chapter if none selected
  useEffect(() => {
    if (course?.chapters?.length && !chapterId) {
      const firstChapter = course.chapters[0];
      navigate(`/learn/${courseSlug}/${firstChapter.id}`, { replace: true });
    }
  }, [course, chapterId, courseSlug, navigate]);

  // Quiz timer countdown
  useEffect(() => {
    // Start timer when quiz is shown and has a time limit
    if (showQuiz && quiz?.timeLimit && attemptId) {
      setTimeRemaining(quiz.timeLimit);

      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev === null || prev <= 1) {
            // Time's up - clear interval
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };
    } else {
      setTimeRemaining(null);
    }
  }, [showQuiz, quiz?.timeLimit, attemptId]);

  // Auto-submit when time runs out
  useEffect(() => {
    if (timeRemaining === 0 && attemptId && !submitQuizMutation.isPending) {
      // Auto-submit the quiz
      const answers = Object.entries(quizAnswers).map(
        ([questionId, selectedOptionIds]) => ({
          questionId,
          selectedOptionIds,
        }),
      );

      submitQuizMutation.mutate(
        { attemptId, answers },
        {
          onSuccess: (result) => {
            setShowQuiz(false);
            setTimeRemaining(null);
            if (result.newBadges?.length) {
              setNewBadges((prev) => [...prev, ...result.newBadges!]);
            }
            if (result.passed) {
              handleMarkComplete();
            }
          },
        },
      );
    }
  }, [timeRemaining, attemptId, quizAnswers, submitQuizMutation]);

  const currentChapterIndex =
    course?.chapters?.findIndex((ch) => ch.id === chapterId) ?? -1;
  const prevChapter =
    currentChapterIndex > 0
      ? course?.chapters?.[currentChapterIndex - 1]
      : null;
  const nextChapter =
    currentChapterIndex < (course?.chapters?.length || 0) - 1
      ? course?.chapters?.[currentChapterIndex + 1]
      : null;
  const totalSections = chapter?.sections?.length ?? 0;
  const isAtFinalSection =
    totalSections === 0 || currentSectionIndex >= totalSections - 1;
  const hasPendingViewedSections =
    (chapterSectionProgress?.totalSections ?? 0) > 0 &&
    (chapterSectionProgress?.viewedSections ?? 0) <
      (chapterSectionProgress?.totalSections ?? 0);
  const viewedSectionIds = useMemo(
    () =>
      chapterSectionProgress?.sections
        .filter((section) => section.isViewed)
        .map((section) => section.sectionId) ?? [],
    [chapterSectionProgress],
  );
  const firstPendingSection = chapterSectionProgress?.sections.find(
    (section) => !section.isViewed,
  );

  const handleGoToFirstPendingSection = () => {
    if (!firstPendingSection) return;
    const index = Math.max(0, firstPendingSection.position - 1);
    setResolvedInitialSection(index);
    handleSectionChange(index);
  };

  const handleMarkComplete = () => {
    if (chapterId) {
      markCompleteMutation.mutate(chapterId, {
        onSuccess: (result) => {
          // Mostrar badges ganados
          if (result.newBadges?.length) {
            setNewBadges(result.newBadges);
          }
          // Mostrar celebración de curso completo
          if (result.isCourseComplete) {
            setShowCourseComplete(true);
          } else if (result.courseProgress) {
            // Check for milestone celebrations (25%, 50%, 75%)
            const milestones = [25, 50, 75];
            for (const milestone of milestones) {
              if (
                result.courseProgress >= milestone &&
                lastMilestone.current < milestone
              ) {
                lastMilestone.current = milestone;
                setShowMilestone(milestone);
                // Auto-hide after 3 seconds
                setTimeout(() => setShowMilestone(null), 3000);
                break;
              }
            }
          }
        },
      });
    }
  };

  const handleStartQuiz = async () => {
    if (quiz) {
      const result = await startQuizMutation.mutateAsync(quiz.id);
      setAttemptId(result.id);
      setShowQuiz(true);
      // Hidratar respuestas guardadas si hay un intento previo en progreso
      setQuizAnswers(
        (result as { savedAnswers?: Record<string, string[]> | null })
          .savedAnswers ?? {},
      );
    }
  };

  // Autosave de respuestas del quiz con debounce
  const [, setQuizLastSavedAt] = useState<Date | null>(null);
  const [, setQuizSaving] = useState(false);
  const quizSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!showQuiz || !attemptId) return;
    if (quizSaveTimeoutRef.current) clearTimeout(quizSaveTimeoutRef.current);
    quizSaveTimeoutRef.current = setTimeout(() => {
      setQuizSaving(true);
      learningApi
        .saveQuizProgress(attemptId, quizAnswers)
        .then(() => setQuizLastSavedAt(new Date()))
        .catch(() => {
          /* swallow: el envío final reconcilia */
        })
        .finally(() => setQuizSaving(false));
    }, 1500);
    return () => {
      if (quizSaveTimeoutRef.current) clearTimeout(quizSaveTimeoutRef.current);
    };
  }, [quizAnswers, showQuiz, attemptId]);

  const handleSubmitQuiz = () => {
    if (attemptId) {
      const answers = Object.entries(quizAnswers).map(
        ([questionId, selectedOptionIds]) => ({
          questionId,
          selectedOptionIds,
        }),
      );

      submitQuizMutation.mutate(
        { attemptId, answers },
        {
          onSuccess: (result) => {
            setShowQuiz(false);
            // Mostrar badges del quiz (como perfect quiz)
            if (result.newBadges?.length) {
              setNewBadges((prev) => [...prev, ...result.newBadges!]);
            }
            if (result.passed) {
              handleMarkComplete();
            }
          },
        },
      );
    }
  };

  const handleOptionSelect = (
    questionId: string,
    optionId: string,
    isMultiple: boolean,
  ) => {
    setQuizAnswers((prev) => {
      if (isMultiple) {
        const current = prev[questionId] || [];
        if (current.includes(optionId)) {
          return {
            ...prev,
            [questionId]: current.filter((id) => id !== optionId),
          };
        }
        return { ...prev, [questionId]: [...current, optionId] };
      }
      return { ...prev, [questionId]: [optionId] };
    });
  };

  if (courseLoading || chapterLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-muted rounded w-48 mb-4 mx-auto" />
          <div className="h-4 bg-muted rounded w-32 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-80 bg-white border-r transform transition-transform duration-300 lg:relative lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-2">
              <Link
                to={`/courses/${courseSlug}`}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4 inline mr-1" />
                Volver al curso
              </Link>
              <button
                className="lg:hidden p-2"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <h2 className="font-semibold line-clamp-2">{course?.title}</h2>
            <div className="mt-2">
              <Progress value={course?.userProgress || 0} className="h-2" />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>{course?.userProgress || 0}% completado</span>
                <span>
                  {course?.chapters?.filter(
                    (ch) => ch.userProgress?.isCompleted,
                  ).length || 0}
                  /{course?.chapters?.length || 0} capítulos
                </span>
              </div>
              {/* Time remaining estimate */}
              {(() => {
                const remainingChapters = course?.chapters?.filter(
                  (ch) => !ch.userProgress?.isCompleted,
                );
                const remainingDuration =
                  remainingChapters?.reduce(
                    (acc, ch) => acc + (ch.videoDuration || 0),
                    0,
                  ) || 0;
                if (
                  remainingDuration > 0 &&
                  (course?.userProgress || 0) < 100
                ) {
                  const hours = Math.floor(remainingDuration / 3600);
                  const minutes = Math.floor((remainingDuration % 3600) / 60);
                  return (
                    <p className="text-xs text-muted-foreground mt-1">
                      ~{hours > 0 ? `${hours}h ` : ""}
                      {minutes}min restantes
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          </div>

          {/* Chapters list */}
          <div className="flex-1 overflow-y-auto p-2">
            {course?.chapters?.map((ch, index) => {
              const isActive = ch.id === chapterId;
              const isCompleted = ch.userProgress?.isCompleted;
              const isLocked = ch.isLocked && !ch.isFree;

              return (
                <button
                  key={ch.id}
                  onClick={() =>
                    !isLocked && navigate(`/learn/${courseSlug}/${ch.id}`)
                  }
                  disabled={isLocked}
                  className={`w-full text-left p-3 rounded-lg mb-1 flex items-center gap-3 transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : isLocked
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-muted"
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isActive
                        ? "bg-primary-foreground text-primary"
                        : isCompleted
                          ? "bg-green-500 text-white"
                          : "bg-muted"
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : isLocked ? (
                      <Lock className="h-4 w-4" />
                    ) : (
                      <span className="text-sm">{index + 1}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{ch.title}</p>
                    {ch.isFree && !isActive && (
                      <Badge variant="outline" className="text-xs mt-1">
                        Vista previa
                      </Badge>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen">
        {/* Top bar with breadcrumb */}
        <header className="sticky top-0 z-30 h-14 bg-white border-b flex items-center px-4">
          <button
            className="lg:hidden p-2 -ml-2"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          {/* Breadcrumb: Curso > Capítulo */}
          <nav className="flex items-center gap-2 ml-2 lg:ml-0 min-w-0">
            <Link
              to={`/courses/${courseSlug}`}
              className="text-sm text-muted-foreground hover:text-foreground truncate max-w-[120px] sm:max-w-[200px]"
              title={course?.title}
            >
              {course?.title}
            </Link>
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span
              className="font-medium truncate max-w-[150px] sm:max-w-[300px]"
              title={chapter?.title}
            >
              {chapter?.title}
            </span>
          </nav>
        </header>

        {/* Payment Status Banner */}
        {chapter?.paymentStatus && chapter.paymentStatus !== "COMPLETED" && (
          <div className="px-4 py-2">
            <PaymentStatusBanner
              status={chapter.paymentStatus}
              message={chapter.paymentMessage}
              holdReleaseDate={chapter.paymentHoldReleaseDate}
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {showQuiz && quiz ? (
            /* Quiz View */
            <div className="max-w-3xl mx-auto p-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold">{quiz.title}</h2>

                    {/* Timer display */}
                    {timeRemaining !== null && (
                      <div
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-lg ${
                          timeRemaining <= 60
                            ? "bg-red-100 text-red-700 animate-pulse"
                            : timeRemaining <= 300
                              ? "bg-amber-100 text-amber-700"
                              : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        <Clock className="h-5 w-5" />
                        <span>
                          {Math.floor(timeRemaining / 60)
                            .toString()
                            .padStart(2, "0")}
                          :{(timeRemaining % 60).toString().padStart(2, "0")}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-6">
                    {quiz.questions.map((question, qIndex) => {
                      const isMultiple = question.type === "MULTIPLE_CHOICE";
                      const groupName = `q-${question.id}`;
                      return (
                        <fieldset
                          key={question.id}
                          className="p-4 border rounded-lg"
                          role={isMultiple ? "group" : "radiogroup"}
                          aria-labelledby={`${groupName}-label`}
                        >
                          <legend
                            id={`${groupName}-label`}
                            className="font-medium mb-4"
                          >
                            {qIndex + 1}. {question.text}
                          </legend>
                          <div className="space-y-2">
                            {question.options.map((option) => {
                              const isSelected = quizAnswers[
                                question.id
                              ]?.includes(option.id);
                              const inputId = `${groupName}-${option.id}`;
                              return (
                                <label
                                  key={option.id}
                                  htmlFor={inputId}
                                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-primary ${
                                    isSelected
                                      ? "border-primary bg-primary/5"
                                      : "hover:border-muted-foreground"
                                  }`}
                                >
                                  <input
                                    id={inputId}
                                    type={isMultiple ? "checkbox" : "radio"}
                                    name={groupName}
                                    checked={!!isSelected}
                                    onChange={() =>
                                      handleOptionSelect(
                                        question.id,
                                        option.id,
                                        isMultiple,
                                      )
                                    }
                                    className="w-5 h-5 accent-primary cursor-pointer"
                                  />
                                  <span>{option.text}</span>
                                </label>
                              );
                            })}
                          </div>
                        </fieldset>
                      );
                    })}
                  </div>

                  {/* Time warning */}
                  {timeRemaining !== null &&
                    timeRemaining <= 60 &&
                    timeRemaining > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
                        <div className="flex items-center gap-2 text-red-700">
                          <AlertCircle className="h-5 w-5" />
                          <span className="font-medium">
                            ¡Menos de un minuto restante! El quiz se enviará
                            automáticamente.
                          </span>
                        </div>
                      </div>
                    )}

                  <div className="flex gap-4 mt-8">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowQuiz(false);
                        setTimeRemaining(null);
                        if (timerRef.current) {
                          clearInterval(timerRef.current);
                          timerRef.current = null;
                        }
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSubmitQuiz}
                      isLoading={submitQuizMutation.isPending}
                      disabled={timeRemaining === 0}
                    >
                      {timeRemaining === 0
                        ? "Enviando..."
                        : "Enviar Respuestas"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            /* Chapter Content */
            <div className="max-w-4xl mx-auto p-6">
              {/* Video */}
              {chapter?.videoUrl && (
                <div className="aspect-video rounded-lg overflow-hidden bg-black mb-6">
                  {hasResumed &&
                    chapter?.userProgress?.lastPosition &&
                    chapter.userProgress.lastPosition > 0 && (
                      <div className="bg-primary-600 text-white text-sm py-1 px-3 text-center">
                        Continuando desde donde lo dejaste
                      </div>
                    )}
                  <ReactPlayer
                    ref={playerRef}
                    url={chapter.videoUrl}
                    width="100%"
                    height="100%"
                    controls
                    playing={false}
                    onReady={() => setIsPlayerReady(true)}
                    onProgress={handleVideoProgress}
                    progressInterval={5000}
                  />
                </div>
              )}

              {/* Content - use SectionPlayer if sections available, otherwise fallback */}
              {chapter?.sections && chapter.sections.length > 0 ? (
                <div className="mb-8">
                  <SectionPlayer
                    sections={chapter.sections}
                    chapterTitle={chapter.title}
                    chapterAudioUrl={chapter.audioUrl}
                    chapterAudioDuration={chapter.audioDuration}
                    initialSection={resolvedInitialSection}
                    onSectionChange={handleSectionChange}
                    onSectionViewed={handleSectionViewed}
                    viewedSectionIds={viewedSectionIds}
                    sectionProgress={chapterSectionProgress?.sections}
                    gating={sectionGating}
                  />
                </div>
              ) : (
                <>
                  {/* Audio Player (legacy chapters without sections) */}
                  {chapter?.audioUrl && (
                    <div className="mb-6">
                      <AudioPlayer
                        audioUrl={chapter.audioUrl}
                        audioDuration={chapter.audioDuration}
                        title={chapter.title}
                      />
                    </div>
                  )}

                  {/* Content (legacy chapters without sections) */}
                  {chapter?.content && (
                    <div className="mb-8">
                      <InteractiveContent
                        html={chapter.content}
                        defaultExpanded={true}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Attachments */}
              {chapter?.attachments && chapter.attachments.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold mb-4">Material Descargable</h3>
                  <div className="space-y-2">
                    {chapter.attachments.map((attachment) => (
                      <a
                        key={attachment.id}
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted transition-colors"
                      >
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <span className="flex-1">{attachment.name}</span>
                        <Download className="h-4 w-4 text-muted-foreground" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-4 border-t pt-6">
                {quiz &&
                  isAtFinalSection &&
                  !chapter?.userProgress?.isCompleted && (
                    <div className="flex flex-col gap-3 w-full">
                      {/* Tarjeta informativa antes de iniciar el quiz */}
                      <div className="rounded-lg border bg-muted/30 p-4">
                        <h4 className="font-semibold mb-2">
                          Antes de comenzar
                        </h4>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          <dt className="text-muted-foreground">
                            Puntaje mínimo
                          </dt>
                          <dd className="font-medium">{quiz.passingScore}%</dd>
                          {quiz.timeLimit ? (
                            <>
                              <dt className="text-muted-foreground">
                                Tiempo límite
                              </dt>
                              <dd className="font-medium">
                                {quiz.timeLimit} min
                              </dd>
                            </>
                          ) : null}
                          {quiz.maxAttempts ? (
                            <>
                              <dt className="text-muted-foreground">
                                Intentos permitidos
                              </dt>
                              <dd className="font-medium">
                                {quiz.maxAttempts}
                              </dd>
                            </>
                          ) : null}
                          {quiz.cooldownMinutes ? (
                            <>
                              <dt className="text-muted-foreground">
                                Espera entre intentos
                              </dt>
                              <dd className="font-medium">
                                {quiz.cooldownMinutes} min
                              </dd>
                            </>
                          ) : null}
                          <dt className="text-muted-foreground">Preguntas</dt>
                          <dd className="font-medium">
                            {quiz.questions.length}
                          </dd>
                        </dl>
                      </div>
                      <div className="flex items-center gap-4">
                        <Button
                          onClick={handleStartQuiz}
                          isLoading={startQuizMutation.isPending}
                          disabled={
                            quiz.canStartAttempt === false ||
                            hasPendingViewedSections
                          }
                        >
                          <PlayCircle className="mr-2 h-4 w-4" />
                          Tomar Quiz
                        </Button>

                        {/* Show attempts remaining */}
                        {quiz.attemptsRemaining !== null &&
                          quiz.attemptsRemaining !== undefined && (
                            <span className="text-sm text-muted-foreground flex items-center gap-1">
                              <AlertCircle className="h-4 w-4" />
                              {quiz.attemptsRemaining === 0
                                ? "Sin intentos restantes"
                                : `${quiz.attemptsRemaining} intento${quiz.attemptsRemaining !== 1 ? "s" : ""} restante${quiz.attemptsRemaining !== 1 ? "s" : ""}`}
                            </span>
                          )}
                      </div>

                      {hasPendingViewedSections && (
                        <div className="flex flex-col gap-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700 sm:flex-row sm:items-center sm:justify-between">
                          <span>
                            Has visto{" "}
                            {chapterSectionProgress?.viewedSections ?? 0} de{" "}
                            {chapterSectionProgress?.totalSections ?? 0}{" "}
                            secciones. Completa todas las secciones del capítulo
                            para tomar el quiz.
                          </span>
                          {firstPendingSection && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleGoToFirstPendingSection}
                              className="border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
                            >
                              Ir a sección {firstPendingSection.position}
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Show cooldown message */}
                      {quiz.cooldownEndsAt && (
                        <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                          <Clock className="h-4 w-4" />
                          <span>
                            Puedes intentar de nuevo a las{" "}
                            {new Date(quiz.cooldownEndsAt).toLocaleTimeString(
                              "es-ES",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </span>
                        </div>
                      )}

                      {/* Show best score if available */}
                      {quiz.bestScore !== null &&
                        quiz.bestScore !== undefined && (
                          <span className="text-sm text-muted-foreground">
                            Mejor puntaje: {quiz.bestScore}%
                            {quiz.bestScore >= quiz.passingScore && (
                              <CheckCircle className="inline ml-1 h-4 w-4 text-green-500" />
                            )}
                          </span>
                        )}
                    </div>
                  )}

                {quiz &&
                  !isAtFinalSection &&
                  !chapter?.userProgress?.isCompleted && (
                    <div className="w-full rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
                      El quiz aparecerá al final del capítulo.
                    </div>
                  )}

                {!quiz && !chapter?.userProgress?.isCompleted && (
                  <div className="flex flex-col gap-3 w-full">
                    <Button
                      onClick={handleMarkComplete}
                      isLoading={markCompleteMutation.isPending}
                      disabled={hasPendingViewedSections}
                      className="w-fit"
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Marcar como completado
                    </Button>
                    {hasPendingViewedSections && (
                      <div className="flex flex-col gap-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700 sm:flex-row sm:items-center sm:justify-between">
                        <span>
                          Completa todas las secciones del capítulo antes de
                          marcarlo como completado.
                        </span>
                        {firstPendingSection && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleGoToFirstPendingSection}
                            className="border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
                          >
                            Ir a sección {firstPendingSection.position}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {chapter?.userProgress?.isCompleted && (
                  <Badge variant="success" className="text-sm py-2 px-4">
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Capítulo completado
                  </Badge>
                )}
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                {prevChapter ? (
                  <Button
                    variant="outline"
                    onClick={() =>
                      navigate(`/learn/${courseSlug}/${prevChapter.id}`)
                    }
                  >
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Anterior
                  </Button>
                ) : (
                  <div />
                )}

                {nextChapter ? (
                  nextChapter.isLocked ? (
                    <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-4 py-2 text-sm text-gray-500">
                      <Lock className="h-4 w-4" />
                      <span>Completa este capítulo para continuar</span>
                    </div>
                  ) : (
                    <Button
                      onClick={() =>
                        navigate(`/learn/${courseSlug}/${nextChapter.id}`)
                      }
                    >
                      Siguiente
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  )
                ) : null}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Celebraciones */}
      {newBadges.length > 0 && (
        <BadgeCelebration badges={newBadges} onClose={() => setNewBadges([])} />
      )}

      {showCourseComplete && course && (
        <CourseCompleteCelebration
          courseId={course.id}
          courseTitle={course.title}
          isOpen={showCourseComplete}
          onClose={() => setShowCourseComplete(false)}
        />
      )}

      {/* Milestone Celebration */}
      {showMilestone && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-8 right-8 z-50"
        >
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-4 rounded-lg shadow-lg">
            <div className="flex items-center gap-3">
              <div className="text-3xl">
                {showMilestone === 25
                  ? "🌟"
                  : showMilestone === 50
                    ? "🔥"
                    : "🚀"}
              </div>
              <div>
                <p className="font-bold text-lg">
                  ¡{showMilestone}% completado!
                </p>
                <p className="text-primary-100 text-sm">
                  {showMilestone === 25
                    ? "¡Gran comienzo! Sigue así"
                    : showMilestone === 50
                      ? "¡Ya llevas la mitad!"
                      : "¡Casi lo logras!"}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
